// UserForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import axios from 'axios';
import './UserForm.css';
import AuthContext from './AuthContext'; // Import the AuthContext

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_PROXY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const UserForm = () => {
  const navigate = useNavigate();
  const { dataUser } = useOutletContext();
  const { customerId } = useParams();
  const { userId } = useParams();
  const { user, emailRequired, setEmailRequired } = useContext(AuthContext); // Use the AuthContext
  const isOwnProfile = user && String(user.id) === String(userId);
  const [formData, setFormData] = useState({
    title: "",
    person_Name: "",
    email: "",
    login: "",
    password: "", // Empty by default
    user_role: "3",
    active: "Yes",
  });

  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    number: false,
    uppercase: false,
    symbol: false,
    noSpaces: true,
  });

  const [error, setError] = useState(null);          // fatal: replaces the form (auth / not found)
  const [formError, setFormError] = useState(null);  // validation / save problems: shown above the form
  const [message, setMessage] = useState(null);
  // WEST-794: email is required and must be unique across all users (excluding this user)
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [emailChecking, setEmailChecking] = useState(false);
  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const [originalPasswordState, setOriginalPasswordState] = useState(""); // Stores initial password state

  useEffect(() => {
    
  }, [user, navigate]);

  useEffect(() => {
    console.log("User object:", user);
    if (!user) {
      setError('User is not authenticated.');
      navigate('/login');
        return;
      }
      // check expiration
      const currentTime = Date.now() / 1000; // in seconds
      if (user.exp && user.exp <= currentTime) {
        // token has expired
        navigate('/login');
      }
            
    if (userId && !dataUser) return;

    if (dataUser && dataUser.length) {
      try {
        const isElevated = user?.role_name === 'Admin' || user?.role_name === 'SuperAdmin';
        if (!isElevated && dataUser[0].id !== parseInt(userId, 10)) {
          throw new Error("Accessing an incorrect user id.");
        }
      } catch (error) {
        setError(error);
      }

      setFormData({
        title: dataUser[0].title || '',
        person_Name: dataUser[0].person_Name || '',
        email: dataUser[0].email || '',
        login: dataUser[0].login || '',
        password: '', // Keep empty on initial load
        user_role: String(dataUser[0].user_Role) || "3",
        active: dataUser[0].active || 'Yes',
      });
    } else if (userId && (!dataUser || dataUser.length === 0)) {
      setError('The requested user does not exist.');
    } else if (dataUser.result && dataUser.result.error) {
      if (dataUser.result.error.indexOf("Unauthorized Access"))
        setError("Unauthorized Access");
    }
  }, [userId, dataUser, navigate]);

  const validatePassword = (password) => {
    const length = password.length >= 10;
    const number = /\d/.test(password);
    const uppercase = /[A-Z]/.test(password);
    const symbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const noSpaces = !/\s/.test(password);

    setPasswordValidation({ length, number, uppercase, symbol, noSpaces });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === 'email') {
      setEmailAvailable(true); // re-checked on blur
    }

    if (name === 'password') {
      if (value) {
        setIsPasswordRequired(true); // Show validation when user starts typing
        validatePassword(value);
      } else {
        setIsPasswordRequired(false); // Hide validation if password is cleared
      }
    }
  };

  const checkEmailAvailability = async (email) => {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) {
      setEmailAvailable(true);
      return;
    }

    setEmailChecking(true);

    try {
      const response = await apiClient.get('/api/user/checkEmail', {
        params: { email: normalized, excludeId: userId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setEmailAvailable(Boolean(response.data && response.data.available));
    } catch (err) {
      console.error('Error checking email:', err);
      setEmailAvailable(false);
    } finally {
      setEmailChecking(false);
    }
  };

  const handleEmailBlur = (e) => {
    const normalized = (e.target.value || '').trim().toLowerCase();
    setFormData((prevData) => ({ ...prevData, email: normalized }));
    checkEmailAvailability(normalized);
  };

  const handleCancelPassword = () => {
    setFormData((prevData) => ({
      ...prevData,
      password: originalPasswordState, // Reset password field
    }));
    setIsPasswordRequired(false); // Hide validation rules
  };

  const isPasswordValid = isPasswordRequired ? Object.values(passwordValidation).every((isValid) => isValid) : true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setMessage(null);
    if (isPasswordRequired && !isPasswordValid) {
      setFormError('Please fix the password validation errors.');
      return;
    }
    const email = (formData.email || '').trim().toLowerCase();
    if (!email) {
      setFormError('Email address is required.');
      return;
    }
    if (!emailAvailable) {
      setFormError('That email address is already in use.');
      return;
    }

    try {
      const payload = {
        ...formData,
        email,
        user_role: String(formData.user_role), // Convert user_role to a string
        customer_id: String(user.customer_id),
        id: userId,
      };
      const response = await apiClient.post(
        '/api/user/update',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const result = response && response.data && response.data.result;
      const problem = result && (result.error || (result.message && result.message.includes('ERROR') ? result.message : null));

      if (problem) {
        if (problem.includes('Email already in use') || (problem.includes('Duplicate') && problem.toLowerCase().includes('email'))) {
          setEmailAvailable(false);
          setFormError('That email address is already in use.');
        } else if (problem.includes('Email is required') || problem.includes('Invalid Email Format')) {
          setFormError('Please enter a valid email address.');
        } else if (problem.includes('must be different from your current password')) {
          setFormError('The new password must be different from the current password.');
        } else if (problem.includes('Duplicate')) {
          setFormError('That username is unavailable.');
        } else {
          setFormError(problem);
        }
      } else if (result && result.message) {
        setMessage(result.message);

        // WEST-794: the forced add-email step is satisfied once the user's own profile saves
        // with an email. Release the navigation lock and send them where their role normally lands.
        if (emailRequired && isOwnProfile) {
          setEmailRequired(false);
          if (user.role_name === 'Admin' || user.role_name === 'SuperAdmin') {
            navigate(`/customer/${customerId}/client/list`);
          }
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormError('An error occurred while submitting the form.');
    }
  };

  if (userId && !dataUser) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (<div className="error">{error}</div>);
  } else {
    return (
      <form onSubmit={handleSubmit} className="user-form" autoComplete="off">
        {emailRequired && isOwnProfile && (
          <div className="message">
            Please add your email address to continue. It is needed to send you a password reset link if you ever forget your password.
          </div>
        )}
        {formError && <div className="error">{formError}</div>}
        {message && <div className="message">{message}</div>}
        <table className="tableMain" border="0" width="100%" cellPadding="2" cellSpacing="2">
          <tbody>
		  <tr>
            <td className="lbltxt">Title:</td>
            <td>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="inputFull fieldsMed"
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Person Name:</td>
            <td>
              <input
                type="text"
                name="person_Name"
                value={formData.person_Name}
                onChange={handleInputChange}
                className="inputFull fieldsMed"
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Email:</td>
            <td>
              <input
                type="email"
                name="email"
                required
                autoComplete="off"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                className="inputFull fieldsMed"
              />
              {emailChecking && <p className="info-message">Checking email...</p>}
              {!emailAvailable && <p className="error">That email address is already in use.</p>}
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Login:</td>
            <td>
            <span>{formData.login}</span>              
            </td>
          </tr>  
            <tr>
              <td className="lbltxt">New password:</td>
              <td>
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="inputFull fieldsMed"
                />
                {!isPasswordRequired && <p className="info-message">Leave blank to keep the current password.</p>}
                {isPasswordRequired && (
                  <div className="password-validation">
                    <ul>
                      <li className={passwordValidation.length ? 'valid' : 'invalid'}>
                        At least 10 characters
                      </li>
                      <li className={passwordValidation.number ? 'valid' : 'invalid'}>
                        At least 1 number
                      </li>
                      <li className={passwordValidation.uppercase ? 'valid' : 'invalid'}>
                        At least 1 uppercase letter
                      </li>
                      <li className={passwordValidation.symbol ? 'valid' : 'invalid'}>
                        At least 1 symbol (!@#$%^&*)
                      </li>
                      <li className={passwordValidation.noSpaces ? 'valid' : 'invalid'}>
                        No spaces
                      </li>
                    </ul>
                    <button type="button" onClick={handleCancelPassword} className="cancel-btn">Cancel</button>
                  </div>
                )}
              </td>
            </tr><tr>
            <td className="lbltxt">Access Level:</td>
            <td>
              {user.role_name === 'User' ? (
                // If user_Role = 1, show read-only text
                <span>{user.role_name}</span>
              ) : (
                // Otherwise, show the <select>
                <select
                  name="user_role"
                  value={String(formData.user_role)}
                  selected={String(formData.role_name)}
                  onChange={handleInputChange}
                  className="inputFull fieldsSm"
                >
                  { (user?.role_name === "Admin" || user?.role_name === "SuperAdmin") && <option value="3">User</option>}
                  { (user?.role_name === "Admin" || user?.role_name === "SuperAdmin")  && <option value="2">Customer Admin</option>}
                  { (user?.role_name === "SuperAdmin")  && <option value="1">Super Admin</option>}
                </select>
              )}
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Active:</td>
            <td>
              {user.role_name === 'User' ? (
                // If user_Role = 1, show read-only text
                <span>{formData.active}</span>
              ) : (
                // Otherwise, show the <select>
                <select
                  name="active"
                  value={formData.active}
                  onChange={handleInputChange}
                  className="inputFull fieldsSm"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              )}
            </td>
          </tr>
            <tr>
              <td></td>
              <td>
                <button type="submit" disabled={!isPasswordValid || !emailAvailable || emailChecking} className="fieldsMed">
                  Update
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </form>
    );
  }
};

export default UserForm;
